import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Info } from 'lucide-react';
import CommitmentTaskBadge from './CommitmentTaskBadge';
import type { CommitmentTaskType, RewardMethodType } from '@clareefai/types';

interface TaskSelection {
  type: CommitmentTaskType;
  requiredCount: number;
}

interface SuccessStepProps {
  email: string;
  rewardMethod: RewardMethodType;
  giftCardAmount: number;
  giftCardCurrency: string;
  discountUnit: string;
  discountAmount: number;
  selectedTasks: TaskSelection[];
  onSendAnother: () => void;
  onDone: () => void;
}

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

const getRewardLabel = (method: RewardMethodType, giftCardAmount: number, giftCardCurrency: string, discountUnit: string, discountAmount: number, tFn: (key: string) => string) => {
  const parts: string[] = [];
  if ((method === 'GIFT_CARD' || method === 'BOTH') && giftCardAmount > 0) {
    parts.push(`${giftCardAmount}${CURRENCY_SYMBOLS[giftCardCurrency] || giftCardCurrency} ${tFn('rewards.specific.preview.giftCard')}`);
  }
  if ((method === 'DISCOUNT' || method === 'BOTH') && discountAmount > 0) {
    const discountLabel = discountUnit === 'percentage' ? `${discountAmount}% discount`
      : discountUnit === 'credits' ? `${discountAmount} credits`
      : discountUnit === 'free_months' ? `${discountAmount} free month(s)`
      : `${discountAmount} ${discountUnit}`;
    parts.push(discountLabel);
  }
  return parts.join(` ${tFn('rewards.or')} `) || tFn('rewards.specific.preview.noReward');
};

const SuccessStep: React.FC<SuccessStepProps> = ({
  email, rewardMethod, giftCardAmount, giftCardCurrency, discountUnit, discountAmount,
  selectedTasks, onSendAnother, onDone,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-8 text-center">
      <div>
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('rewards.specific.success.title')}</h3>
        <p className="text-gray-500">{t('rewards.specific.success.subtitle')}</p>
      </div>

      <div className="bg-gradient-to-br from-[#4339A0]/5 to-indigo-50 border border-[#4339A0]/20 rounded-xl p-6 max-w-md mx-auto text-left space-y-3">
        <div className="text-sm">
          <p className="text-gray-500">{t('common.email')}</p>
          <p className="font-medium text-gray-900">{email}</p>
        </div>
        <div className="text-sm">
          <p className="text-gray-500">{t('common.rewards')}</p>
          <p className="font-medium text-gray-900">
            {getRewardLabel(rewardMethod, giftCardAmount, giftCardCurrency, discountUnit, discountAmount, t)}
          </p>
        </div>
        <div className="text-sm">
          <p className="text-gray-500 mb-1.5">{t('rewards.specific.success.commitment')}</p>
          <div className="flex flex-wrap gap-2">
            {selectedTasks.map((task) => (
              <CommitmentTaskBadge key={task.type} type={task.type} requiredCount={task.requiredCount} />
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-md mx-auto">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-left text-sm text-blue-700">
            <p className="font-medium mb-1">{t('rewards.specific.success.whatNext')}</p>
            <ul className="space-y-1 text-xs">
              <li>{t('rewards.specific.success.nextStep1')}</li>
              <li>{t('rewards.specific.success.nextStep2')}</li>
              <li>{t('rewards.specific.success.nextStep3')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-4 pt-2">
        <button
          onClick={onSendAnother}
          className="px-6 py-2.5 text-sm font-medium text-[#4339A0] bg-[#4339A0]/10 hover:bg-[#4339A0]/20 rounded-lg transition-colors"
        >
          {t('rewards.specific.success.sendAnother')}
        </button>
        <button
          onClick={onDone}
          className="px-6 py-2.5 text-sm font-medium text-white bg-[#4339A0] hover:bg-[#3d2f8f] rounded-lg transition-colors"
        >
          {t('common.done')}
        </button>
      </div>
    </div>
  );
};

export default SuccessStep;
